import {
  NbaActionCodes,
  NbaActionSubCategories,
} from '@/src/types/NextBestAction';
import { D2VDownloadCard } from '../../ActionCentre/Banners/ChargeAnytime/D2VDownloadCard';
import { CheckContactDetails } from '../../ActionCentre/Banners/CheckContactDetails/CheckContactDetails';
import {
  EnergyTrackerFirstTimeBanner,
  EnergyTrackerReturningBanner,
} from '../../ActionCentre/Banners/EnergyTracker/EnergyTrackerBanner';
import { HomeRecoverBanner } from '../../ActionCentre/Banners/HomeRecover/HomeRecover';
import { DirectDebitReduction } from '../../ActionCentre/Banners/Payments/DirectDebitReduction';
import { DirectDebitSetup } from '../../ActionCentre/Banners/Payments/DirectDebitSetup';
import { FixedPlanRenewalCard } from '../../ActionCentre/Banners/Renewal/FixedPlanRenewalCard';
import { SmartMeterBookingBookingNowAvailable } from '../../ActionCentre/Banners/SmartMeterBooking/SmartMeterBookingBookingNowAvailable';
import { SmartMeterBookingRecerts } from '../../ActionCentre/Banners/SmartMeterBooking/SmartMeterBookingRecerts';
import { SolarPanelsCard } from '../../ActionCentre/Banners/Solar/SolarPanels';
import { BannerId, banners } from '../../ActionCentre/Banners/banners';
import { useAccountContext } from '@ovotech/ui-tools';
import { useContractsQueryLegacy } from '@/src/api';
import { useMemo } from 'react';
import { lossBannerToUse } from './utils/lossBannerToUse';
import { homeplanBannerToUse } from './utils/homeplanBannerToUse';

export type NbaActionCodeId =
  | NbaActionCodes
  | `${NbaActionCodes}_${NbaActionSubCategories}`;

type ActionCard = {
  name: BannerId;
  component: React.ComponentType<any>;
  featureFlag?: string;
};

export type NbaActionCardsMap = Partial<
  Record<NbaActionCodeId, ActionCard | null>
> & { [key: string]: ActionCard | null };

export const nbaBannersMap: NbaActionCardsMap = {
  anytime_ovo_001: {
    name: 'd2VDownloadCard',
    component: D2VDownloadCard,
  },
  check_contact_details_ovo_001: {
    name: 'checkContactDetails',
    component: CheckContactDetails,
  },
  greenlight_new_ovo_001: {
    name: 'greenlightNew',
    component: EnergyTrackerFirstTimeBanner,
  },
  greenlight_view_again_ovo_001: {
    name: 'greenlightViewAgain',
    component: EnergyTrackerReturningBanner,
  },
  in_renewal_window_ovo_001: {
    name: 'renewalWindow',
    component: FixedPlanRenewalCard,
  },
  smart_booking_ovo_001: {
    name: 'smartMeterBookingRecerts',
    component: SmartMeterBookingRecerts,
  },
  smart_booking_ovo_001_recerts: {
    name: 'smartMeterBookingRecerts',
    component: SmartMeterBookingRecerts,
  },
  smart_booking_ovo_001_booking_now_available: {
    name: 'smartMeterBookingBookingNowAvailable',
    component: SmartMeterBookingBookingNowAvailable,
  },
  solar_install_ovo_001: {
    name: 'solarPanels',
    component: SolarPanelsCard,
  },
  dd_set_up_ovo_001: {
    name: 'directDebitSetup',
    component: DirectDebitSetup,
  },
  dd_change_ovo_001_dd_decrease: {
    name: 'directDebitReduction',
    component: DirectDebitReduction,
  },
  sell_home_recover_ovo_001: {
    name: 'homeRecover',
    component: HomeRecoverBanner,
  },
};

const getActionCard = (
  bannerId: BannerId | null,
): React.ComponentType<any> | undefined => {
  if (!bannerId || !banners[bannerId]) return;
  return banners[bannerId];
};

export const useHardcodedNbaActionCards = (): NbaActionCardsMap => {
  const { accountId } = useAccountContext();
  const contractQuery = useContractsQueryLegacy(accountId);

  const lossBannerId = contractQuery?.data
    ? lossBannerToUse(contractQuery.data)
    : null;

  const homeplanBannerId = contractQuery?.data
    ? homeplanBannerToUse(contractQuery.data)
    : null;

  const lossComponent = getActionCard(lossBannerId);
  const homeplanComponent = getActionCard(homeplanBannerId);

  return useMemo(() => {
    return {
      ...(lossComponent && lossBannerId
        ? {
            lost_members_ovo_001: {
              name: lossBannerId,
              component: lossComponent,
            },
          }
        : {}),
      ...(homeplanComponent && homeplanBannerId
        ? {
            sell_ovo_homeplan_ovo_001_incentive_xsell: {
              name: homeplanBannerId,
              component: homeplanComponent,
            },
          }
        : {}),
      ...nbaBannersMap,
    };
  }, [homeplanBannerId, lossBannerId, homeplanComponent, lossComponent]);
};
